/**
 * Pure step-advance / progress helpers for the patient package stepper
 * (PackageRunView). Kept in a plain .ts module (no React) so the ordering,
 * resume, and completion logic is unit-testable under the node-env vitest setup.
 *
 * The server drives resume by returning `nextItemId`, but the stepper still needs
 * a deterministic, ordered view of the run to render "Step i of N" and the
 * progress list — this module derives that from the run's items[] alone.
 */

/** Minimal shape needed to compute progress (subset of RunItemMeta from api.ts). */
export interface ProgressItem {
  itemId:      string;
  sortOrder:   number;
  completedAt: string | null;
}

export interface RunProgress<T extends ProgressItem> {
  /** Items sorted by sortOrder (ascending) — the render order. */
  ordered:        T[];
  total:          number;
  completedCount: number;
  /** The first not-yet-completed item in order, or null when the run is done. */
  current:        T | null;
  /** 0-based index of `current` within `ordered`; equals `total` when done. */
  currentIndex:   number;
  /** 1-based step number for display ("Step {stepNumber} of {total}"). */
  stepNumber:     number;
  /** True when every item is completed. */
  allDone:        boolean;
}

/**
 * Derive ordered progress from a run's items. Sorts by sortOrder, finds the first
 * incomplete item (resume point), and reports counts. Never mutates the input.
 */
export function computeRunProgress<T extends ProgressItem>(items: readonly T[]): RunProgress<T> {
  const ordered = items.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const total = ordered.length;
  const completedCount = ordered.filter((i) => i.completedAt !== null).length;
  const currentIndex = ordered.findIndex((i) => i.completedAt === null);
  const allDone = currentIndex === -1;
  const current = allDone ? null : (ordered[currentIndex] as T);
  const resolvedIndex = allDone ? total : currentIndex;
  return {
    ordered,
    total,
    completedCount,
    current,
    currentIndex: resolvedIndex,
    stepNumber: allDone ? total : resolvedIndex + 1,
    allDone,
  };
}
